import { BAOCustomElement } from '@bao/custom-element'
import { setInitialised, addWheelControls } from '@bao/carousel'

const KeenSlider = window.KeenSlider

const DRAG_THRESHOLD = 5
const MEDIA_SCALE_LIMIT = 0.9
const ACTIVE_MEDIA_SCALE_BOOST = 0.3

class FeaturedCarousel extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.style.setProperty(
      '--Media_MaxScale',
      Math.max(1, MEDIA_SCALE_LIMIT + ACTIVE_MEDIA_SCALE_BOOST),
    )

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    if (!this.els.slide?.exists) return
    if (!this.els['content-swipe']?.exists) return

    const contentEl = this.els['content-swipe'].element

    return new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: true,
        mode: 'snap',
        slides: {
          origin: 'center',
          perView: 'auto',
          spacing: this.dataset.spacing || 1,
        },
        detailsChanged: (instance) => {
          this.els.slide.elements.forEach((slideEl, index) => {
            const matchingSlide = instance.track.details.slides[index]

            if (!matchingSlide) return

            // Calculate the distance between the centre of the slide
            // and the centre of the carousel viewport
            const slideDistance = Math.abs(
              matchingSlide.distance + matchingSlide.size / 2 - 0.5,
            )

            // Scale down progressively as the slide moves away from the centre
            const mediaScaleAmount = Math.max(
              MEDIA_SCALE_LIMIT * (1 - slideDistance),
              0,
            )

            // Smoothly apply the additional scale as the slide approaches centre
            const centreProximity = Math.max(
              0,
              1 - slideDistance / matchingSlide.size,
            )

            const mediaScaleBoost = ACTIVE_MEDIA_SCALE_BOOST * centreProximity

            const mediaScaleAmountWithBoost =
              mediaScaleAmount + mediaScaleBoost

            slideEl.style.setProperty(
              '--Media_Transform',
              `scale(${mediaScaleAmountWithBoost})`,
            )

            const isActive = instance.track.details.abs === matchingSlide.abs

            this.els.content.elements[index]?.setAttribute(
              'aria-hidden',
              isActive ? 'false' : 'true',
            )
          })
        },
      },
      [
        setInitialised(),
        addWheelControls('horizontal'),
        addWheelControls('horizontal', {
          element: contentEl,
        }),
        this.addContentDragControls(contentEl),
      ],
    )
  }

  addContentDragControls (element) {
    return (carousel) => {
      let pointerId = null
      let startPosition = null
      let dragActive = false
      let suppressClick = false

      const dispatch = (name, position) => {
        carousel.container.dispatchEvent(
          new CustomEvent(name, {
            detail: {
              x: position.x,
              y: position.y,
            },
          }),
        )
      }

      const resetDrag = () => {
        pointerId = null
        startPosition = null
        dragActive = false
      }

      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        pointerId = event.pointerId

        startPosition = {
          x: event.pageX,
          y: event.pageY,
        }

        dragActive = false

        element.setPointerCapture?.(event.pointerId)
      }

      const onPointerMove = (event) => {
        if (event.pointerId !== pointerId || !startPosition) return

        const position = {
          x: event.pageX,
          y: event.pageY,
        }

        const distanceX = position.x - startPosition.x
        const distanceY = position.y - startPosition.y

        if (!dragActive) {
          if (
            Math.abs(distanceX) < DRAG_THRESHOLD &&
            Math.abs(distanceY) < DRAG_THRESHOLD
          ) {
            return
          }

          if (Math.abs(distanceY) > Math.abs(distanceX)) {
            element.releasePointerCapture?.(event.pointerId)

            resetDrag()

            return
          }

          dragActive = true

          dispatch('ksDragStart', startPosition)
        }

        if (event.cancelable) {
          event.preventDefault()
        }

        dispatch('ksDrag', position)
      }

      const onPointerEnd = (event) => {
        if (event.pointerId !== pointerId) return

        if (dragActive) {
          dispatch('ksDragEnd', {
            x: event.pageX,
            y: event.pageY,
          })

          suppressClick = true
        }

        if (element.hasPointerCapture?.(event.pointerId)) {
          element.releasePointerCapture(event.pointerId)
        }

        resetDrag()
      }

      const onClick = (event) => {
        if (!suppressClick) return

        event.preventDefault()
        event.stopPropagation()

        suppressClick = false
      }

      carousel.on('created', () => {
        element.style.touchAction = 'pan-y'

        element.addEventListener('pointerdown', onPointerDown)
        element.addEventListener('pointermove', onPointerMove, {
          passive: false,
        })
        element.addEventListener('pointerup', onPointerEnd)
        element.addEventListener('pointercancel', onPointerEnd)
        element.addEventListener('click', onClick, true)
      })

      carousel.on('destroyed', () => {
        element.removeEventListener('pointerdown', onPointerDown)
        element.removeEventListener('pointermove', onPointerMove)
        element.removeEventListener('pointerup', onPointerEnd)
        element.removeEventListener('pointercancel', onPointerEnd)
        element.removeEventListener('click', onClick, true)
      })
    }
  }
}

if (!customElements.get('featured-carousel')) {
  customElements.define('featured-carousel', FeaturedCarousel)
}
